import { Text, Title, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, type Theme } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { CollapsiblePageHeader } from "../../../components/CollapsiblePageHeader";
import { IconButton } from "../../../components/IconButton";
import { PayRowLabel } from "../../../Pay/PayRowLabel";
import { ShiftQualificationLabel } from "../../../Shift/QualificationLabel";
import { getPayTextColor } from "../../components/getPayTextColor";
import { ShiftStatusPill, type ShiftStatusPillStatus } from "../../components/ShiftStatusPill";
import { SHIFT_HISTORY_FULL_PATH } from "../../path";
import { type MissedPunchRequestStatus } from "../types";
import { PastShiftOptionsBottomSheet } from "./PastShiftOptionsBottomSheet";

const ERROR_STATUSES = new Set<MissedPunchRequestStatus>(["declined", "expired"]);

function getBackgroundGradient(
  theme: Theme,
  missedPunchRequestStatus: MissedPunchRequestStatus,
  isVerified: boolean
): string | undefined {
  if (missedPunchRequestStatus === "pending") {
    return theme.palette.shift?.inreview?.gradient;
  }

  if (ERROR_STATUSES.has(missedPunchRequestStatus) || !isVerified) {
    return theme.palette.shift?.unverified?.gradient;
  }

  return theme.palette.shift?.verified?.gradient;
}

function getPillStatus(
  missedPunchRequestStatus: MissedPunchRequestStatus,
  isUnverifiedWithoutMissedPunchRequest: boolean
): ShiftStatusPillStatus | "none" {
  if (isUnverifiedWithoutMissedPunchRequest) {
    return "unverified";
  }

  return missedPunchRequestStatus;
}

function formatAdjustmentAmount(amountInDollars: number): string {
  const formattedAmount = formatDollarsAsUsd(Math.abs(amountInDollars));
  return amountInDollars >= 0 ? `(+${formattedAmount})` : `(-${formattedAmount})`;
}

interface PastShiftDetailsHeaderProps {
  shiftId: string;
  dateLabel: string;
  qualificationName: string;
  missedPunchRequestStatus: MissedPunchRequestStatus;
  isVerified: boolean;
  totalPayInDollars: number;
  hourlyRateInDollars: number;
  requestedAmountInDollars?: number;
  isFullShiftPayment: boolean;
  scrollContainerRef: React.RefObject<HTMLElement | null>;
}

export function PastShiftDetailsHeader(props: PastShiftDetailsHeaderProps) {
  const {
    shiftId,
    dateLabel,
    qualificationName,
    missedPunchRequestStatus,
    isVerified,
    totalPayInDollars,
    hourlyRateInDollars,
    requestedAmountInDollars,
    isFullShiftPayment,
    scrollContainerRef,
  } = props;

  const optionsModalState = useModalState();

  const isUnverifiedWithoutMissedPunchRequest = !isVerified && missedPunchRequestStatus === "none";
  const showTotalPay = isVerified || missedPunchRequestStatus !== "none";
  const pillStatus = getPillStatus(missedPunchRequestStatus, isUnverifiedWithoutMissedPunchRequest);

  return (
    <>
      <CollapsiblePageHeader
        scrollContainerRef={scrollContainerRef}
        fallbackGoBackPath={SHIFT_HISTORY_FULL_PATH}
        rightAction={
          <IconButton
            iconType="ellipsis"
            variant="outlined"
            aria-label="Options"
            onClick={() => {
              optionsModalState.openModal();
            }}
          />
        }
        collapsedTitle={
          <Text semibold variant="h6" align="center" sx={{ textWrap: "balance", flexGrow: 1 }}>
            {dateLabel}
          </Text>
        }
        headerSx={(theme) => ({
          background: getBackgroundGradient(theme, missedPunchRequestStatus, isVerified),
        })}
        topRowSx={{ paddingX: 6 }}
      >
        <Stack spacing={3} sx={{ paddingX: 6, paddingBottom: 7 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <ShiftQualificationLabel qualificationText={qualificationName} variant="body1" />
            {pillStatus !== "none" && <ShiftStatusPill status={pillStatus} />}
          </Stack>

          <Title component="h3" variant="h3">
            {dateLabel}
          </Title>

          {showTotalPay ? (
            <PayRowLabel
              primaryAmount={formatDollarsAsUsd(
                isFullShiftPayment &&
                  isDefined(requestedAmountInDollars) &&
                  requestedAmountInDollars > 0
                  ? requestedAmountInDollars
                  : totalPayInDollars
              )}
              hourlyPay={formatDollarsAsUsd(hourlyRateInDollars)}
              adjustmentAmount={
                isDefined(requestedAmountInDollars) &&
                requestedAmountInDollars > 0 &&
                !isFullShiftPayment
                  ? formatAdjustmentAmount(requestedAmountInDollars)
                  : undefined
              }
              primaryAmountSx={
                isFullShiftPayment
                  ? (theme) => ({ color: getPayTextColor(theme, missedPunchRequestStatus) })
                  : undefined
              }
              adjustmentAmountSx={
                isFullShiftPayment
                  ? undefined
                  : (theme) => ({ color: getPayTextColor(theme, missedPunchRequestStatus) })
              }
            />
          ) : (
            <Stack direction="row" justifyContent="flex-end">
              <Text semibold variant="h5">
                {`${formatDollarsAsUsd(hourlyRateInDollars)}/hr`}
              </Text>
            </Stack>
          )}
        </Stack>
      </CollapsiblePageHeader>

      <PastShiftOptionsBottomSheet modalState={optionsModalState} shiftId={shiftId} />
    </>
  );
}
